import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    AbuseReportType,
    CommunityCommentContract,
    CommunityContentContract,
    CommunityItemContract,
    EntityType,
    LikeType,
    LinkedContentObjectsContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { RoutingHelpersService } from '@polaris-customerweb/sharedcomponents.shared';
import { cloneDeep } from 'lodash-es';
import { Observable, of, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { RemoveForceCommunityItemRefresh } from 'src/app/components/community/activity/community-item.actions';
import { selectCommunityForceRefreshList } from 'src/app/components/community/activity/community-item.reducer';
import { AppState } from 'src/app/models/app-state';
import { CommunityFeedback } from 'src/app/models/community-feedback';
import { CommunityItem } from 'src/app/models/community-item';
import { CommunityItemSearchFilter } from 'src/app/models/community-item-search-filter';
import { ContentCollection } from 'src/app/models/content-collection';

export interface GetMyCommunityItemsOptions {
    includeFullDetail?: boolean;
    includeDeleted?: boolean;
    modifiedAfter?: Date;
    receivedAfter?: Date;
}

interface CopyCommunityItemResponse {
    entityType: EntityType;
    originalId: string;
    copyId: string;
    wasCopied: boolean;
}

@Injectable({ providedIn: 'root' })
export class CommunityItemResourceService {
    private communityUrl: string;
    private myCommunityUrl: string;
    private communityItemCache: Record<
        string,
        { item: CommunityItemContract; timestamp: number } | undefined
    > = {};
    private rideEventContentCache: Record<
        string,
        { item: LinkedContentObjectsContract; timestamp: number } | undefined
    > = {};
    private collectionContentCache: Record<
        string,
        { item: LinkedContentObjectsContract; timestamp: number } | undefined
    > = {};
    private rideEventCollectionContentCache: Record<
        string,
        { item: LinkedContentObjectsContract; timestamp: number } | undefined
    > = {};
    private CACHE_FOR_SECONDS = 300;

    constructor(
        configService: ConfigService,
        private http: HttpClient,
        store: Store<AppState>,
        private routingHelpersService: RoutingHelpersService
    ) {
        this.communityUrl = routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'community-items'
        );
        this.myCommunityUrl = routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'my-community-items'
        );
        store
            .select(selectCommunityForceRefreshList)
            .subscribe((shouldForceRefreshList) => {
                if (shouldForceRefreshList.length > 0) {
                    shouldForceRefreshList.forEach((contentId) => {
                        this.removeCaches(contentId);
                    });
                    store.dispatch(new RemoveForceCommunityItemRefresh());
                }
            });
    }

    //#region Get community item and content
    /**
     * @param filter
     * @param isAuthenticated
     * @description - GET /community-items
     */
    getCommunityItems(
        filter: CommunityItemSearchFilter,
        isAuthenticated: boolean
    ): Observable<CommunityItem[]> {
        let params = new HttpParams();
        if (!isAuthenticated) {
            params = params.set('skipAuthorization', 'true');
        }

        if (filter.endDate) {
            params = params.set('endDate', String(filter.endDate));
        }
        if (filter.eventEndDate) {
            params = params.set(
                'eventEndDate',
                String(filter.eventEndDate.toISOString())
            );
        }
        if (filter.eventStartDate) {
            params = params.set(
                'eventStartDate',
                String(filter.eventStartDate.toISOString())
            );
        }
        if (filter.includeFullDetail) {
            params = params.set(
                'includeFullDetail',
                String(filter.includeFullDetail)
            );
        }
        if (filter.lat) {
            params = params.set('lat', String(filter.lat));
        }
        if (filter.limit) {
            params = params.set('limit', String(filter.limit));
        }
        if (filter.lng) {
            params = params.set('lon', String(filter.lng));
        }
        if (filter.minimumLength) {
            params = params.set('minLength', String(filter.minimumLength));
        }
        if (filter.maximumLength) {
            params = params.set('maxLength', String(filter.maximumLength));
        }
        if (filter.minimumRating) {
            params = params.set('minRating', String(filter.minimumRating));
        }
        if (filter.maximumRating) {
            params = params.set('maxRating', String(filter.maximumRating));
        }
        if (filter.pagination.isPaginated) {
            params = params.set('page', String(filter.pagination.page));
            params = params.set('size', String(filter.pagination.size));
        }
        if (filter.radiusInMeters) {
            params = params.set('metersRadius', String(filter.radiusInMeters));
        }
        if (filter.search) {
            params = params.set('search', String(filter.search));
        }
        if (filter.sort) {
            params = params.set('sort', filter.sort);
        }
        if (filter.startDate) {
            params = params.set(
                'startDate',
                String(filter.startDate.toISOString())
            );
        }
        // community item type supports multiple parameters (ex: ?type=TrackedRide&type=PlannedRide) (omit for all).
        if (filter.type) {
            filter.type.forEach(
                (type) => (params = params.append('type', String(type)))
            );
        }
        // The community item vehicle type(s) supports multiple parameters (ex: ?vehicleType = ATV & vehicleType=ORV50)
        // (omit for all).
        if (filter.vehicleTypes) {
            filter.vehicleTypes.forEach(
                (vehicleType) =>
                    (params = params.set('vehicleType', vehicleType))
            );
        }

        // TODO: update models to handle includeFullDetail false
        return this.http
            .get<CommunityItemContract[]>(this.communityUrl, { params })
            .pipe(
                map((items) =>
                    items.map((contract) => new CommunityItem(contract))
                )
            );
    }

    /**
     * @param communityItemId
     * @param isAuthenticated
     * @description - GET /community-items/{id}
     */
    getCommunityItemById(
        communityItemId: string,
        isAuthenticated: boolean
    ): Observable<CommunityItem> {
        let params = new HttpParams();

        if (!isAuthenticated) {
            params = params.set('skipAuthorization', 'true');
        }

        const cachedContract = this.communityItemCache[communityItemId];
        if (cachedContract !== undefined) {
            if (
                Date.now() - cachedContract.timestamp <
                this.CACHE_FOR_SECONDS * 1000
            ) {
                return of(new CommunityItem(cachedContract.item));
            } else {
                this.removeCaches(communityItemId);
            }
        }

        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId
        );
        return this.http.get<CommunityItemContract>(url, { params }).pipe(
            tap((contract) => {
                this.communityItemCache[communityItemId] = {
                    item: cloneDeep(contract),
                    timestamp: Date.now(),
                };
            }),
            map((contract) => new CommunityItem(contract))
        );
    }

    /**
     * @param communityItemId
     * @param rideEventId
     * @description - GET /community-items/{id}/events/{rideEventId}/contents
     */
    getRideEventContents(
        communityItemId: string,
        rideEventId: string
    ): Observable<ContentCollection> {
        let params = new HttpParams();
        params = params.set('skipAuthorization', 'true');

        const key = `${communityItemId}${rideEventId}`;
        const cachedContract = this.rideEventContentCache[key];
        if (cachedContract !== undefined) {
            if (
                Date.now() - cachedContract.timestamp <
                this.CACHE_FOR_SECONDS * 1000
            ) {
                return of(new ContentCollection(cachedContract.item));
            } else {
                this.rideEventContentCache[key] = undefined;
            }
        }

        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'events',
            rideEventId,
            'contents'
        );
        return this.http
            .get<LinkedContentObjectsContract>(url, { params })
            .pipe(
                tap(
                    (contract) =>
                        (this.rideEventContentCache[key] = {
                            item: cloneDeep(contract),
                            timestamp: Date.now(),
                        })
                ),
                map(
                    (collection) =>
                        new ContentCollection(collection, (collectionId) => {
                            return this.getRideEventCollectionContents(
                                communityItemId,
                                rideEventId,
                                collectionId
                            );
                        })
                )
            );
    }

    /**
     * @param communityItemId
     * @param collectionId
     * @description - GET /community-items/{id}/collections/{collectionId}/contents
     */
    getCollectionContents(
        communityItemId: string,
        collectionId: string
    ): Observable<ContentCollection> {
        let params = new HttpParams();
        params = params.set('skipAuthorization', 'true');

        const key = `${communityItemId}${collectionId}`;
        const cachedContract = this.collectionContentCache[key];
        if (cachedContract !== undefined) {
            if (
                Date.now() - cachedContract.timestamp <
                this.CACHE_FOR_SECONDS * 1000
            ) {
                return of(new ContentCollection(cachedContract.item));
            } else {
                this.collectionContentCache[key] = undefined;
            }
        }

        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'collections',
            collectionId,
            'contents'
        );
        return this.http
            .get<LinkedContentObjectsContract>(url, { params })
            .pipe(
                tap(
                    (contract) =>
                        (this.collectionContentCache[key] = {
                            item: cloneDeep(contract),
                            timestamp: Date.now(),
                        })
                ),
                map((collection) => new ContentCollection(collection))
            );
    }

    /**
     * @param communityItemId
     * @param rideEventId
     * @param collectionId
     * @description - GET /community-items/{id}/events/{rideEventId}/collections/{collectionId}/contents
     */
    getRideEventCollectionContents(
        communityItemId: string,
        rideEventId: string,
        collectionId: string
    ): Observable<ContentCollection> {
        let params = new HttpParams();
        params = params.set('skipAuthorization', 'true');

        const key = `${communityItemId}${rideEventId}${collectionId}`;
        const cachedContract = this.rideEventCollectionContentCache[key];
        if (cachedContract !== undefined) {
            if (
                Date.now() - cachedContract.timestamp <
                this.CACHE_FOR_SECONDS * 1000
            ) {
                return of(new ContentCollection(cachedContract.item));
            } else {
                this.rideEventCollectionContentCache[key] = undefined;
            }
        }

        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'events',
            rideEventId,
            'collections',
            collectionId,
            'contents'
        );
        return this.http
            .get<LinkedContentObjectsContract>(url, { params })
            .pipe(
                tap((contract) => {
                    this.rideEventCollectionContentCache[key] = {
                        item: cloneDeep(contract),
                        timestamp: Date.now(),
                    };
                }),
                map((collection) => new ContentCollection(collection))
            );
    }
    //#endregion

    //#region My community items
    /**
     * @param options
     * @description - GET /my-community-items/contents
     */
    getMyCommunityItems(
        options?: GetMyCommunityItemsOptions
    ): Observable<CommunityItem[]> {
        // Note: CVC has a GET endpoint for /my-community-items, but it returns collection-like contracts that we don't
        // support. This endpoint is an exact replacement that returns CommunityItemContracts.
        const url = this.routingHelpersService.combineUrl(
            this.myCommunityUrl,
            'contents'
        );

        let params = new HttpParams();
        params = params.set(
            'includeDeleted',
            String(Boolean(options?.includeDeleted))
        );

        if (options) {
            if (options.includeFullDetail) {
                params = params.set(
                    'includeFullDetail',
                    String(options.includeFullDetail)
                );
            }
            if (options.receivedAfter) {
                params = params.set(
                    'receivedAfter',
                    options.receivedAfter.toISOString()
                );
            }
            if (options.modifiedAfter) {
                params = params.set(
                    'modifiedAfter',
                    options.modifiedAfter.toISOString()
                );
            }
        }

        return this.http
            .get<CommunityItemContract[]>(url, { params })
            .pipe(
                map((contracts) =>
                    contracts.map((contract) => new CommunityItem(contract))
                )
            );
    }

    /** @description - GET /my-community-items/{id} */
    getMyCommunityItemById(): Observable<Error> {
        // not supported because this endpoint returns a CommunityItemCreateContract and a CommunityItem is expected
        return throwError(
            () => new Error('Not supported. Get all items and find by ID')
        );
    }

    /**
     * @param communityItem
     * @description - POST /my-community-items
     */
    createCommunityItem(
        communityItem: CommunityItem
    ): Observable<CommunityItem> {
        return this.http
            .post<{
                id: string;
            }>(this.myCommunityUrl, communityItem.toCommunityItemCreateDto())
            .pipe(
                map((response) => {
                    const newItem = communityItem.clone();
                    newItem.id = response.id;
                    return newItem;
                })
            );
    }

    /**
     * @param communityItem
     * @description - PUT /my-community-items/{id}
     */
    updateCommunityItem(
        communityItem: CommunityItem
    ): Observable<CommunityItem> {
        const updatedCommunityItem = communityItem.clone();
        updatedCommunityItem.modificationDate = new Date();
        const communityItemContract = communityItem.toCommunityItemCreateDto();
        this.removeCaches(communityItem.id);
        const url = this.routingHelpersService.combineUrl(
            this.myCommunityUrl,
            communityItem.id
        );

        return this.http
            .put<undefined>(url, communityItemContract)
            .pipe(map(() => updatedCommunityItem));
    }

    /**
     * @param communityItemId
     * @description - DELETE /my-community-items/{id}
     */
    deleteCommunityItem(communityItemId: string): Observable<void> {
        this.removeCaches(communityItemId);
        return this.http.delete<undefined>(
            this.routingHelpersService.combineUrl(
                this.myCommunityUrl,
                communityItemId
            )
        );
    }
    //#endregion

    /**
     * @param communityItemId
     * @description - Copy a community item to a user's account. Returns the new ID of the copied item or
     * undefined on error.
     * POST /community-items/{id}/copy
     */
    copyCommunityItem(communityItemId: string): Observable<string> {
        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'copy'
        );

        return this.http.post<CopyCommunityItemResponse>(url, {}).pipe(
            map((response) => {
                if (!response.wasCopied) {
                    return '';
                } else {
                    return response.copyId;
                }
            })
        );
    }

    //#region Ratings, likes and abuse
    /**
     * @param communityItemId
     * @param rating
     * @description - PUT /community-items/{id}/ratings
     */
    rate(
        communityItemId: string,
        rating: number
    ): Observable<CommunityFeedback> {
        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'ratings'
        );
        const params = new HttpParams().set('rating', String(rating));
        this.removeCaches(communityItemId);

        return this.http
            .put<CommunityContentContract>(url, {}, { params })
            .pipe(
                map(
                    (communityContent) =>
                        new CommunityFeedback(communityContent)
                )
            );
    }

    /**
     * @param communityItemId
     * @param likeType
     * @description - PUT /community-items/{id}/likes
     */
    like(
        communityItemId: string,
        likeType: LikeType
    ): Observable<CommunityFeedback> {
        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'likes'
        );
        const params = new HttpParams().set('type', likeType);
        this.removeCaches(communityItemId);

        return this.http
            .put<CommunityContentContract>(url, {}, { params })
            .pipe(
                map(
                    (communityContent) =>
                        new CommunityFeedback(communityContent)
                )
            );
    }

    /**
     * @param communityItemId
     * @param abuseType
     * @description - PUT /community-items/{id}/abuse-reports
     */
    reportAbuse(
        communityItemId: string,
        abuseType: AbuseReportType = 'None'
    ): Observable<undefined> {
        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'abuse-reports'
        );
        const params = new HttpParams().set('type', abuseType);
        this.removeCaches(communityItemId);

        return this.http.put<undefined>(url, {}, { params });
    }
    //#endregion

    //#region Comments
    /**
     * @param communityItemId
     * @param comment
     * @description - POST /community-items/{id}/comments
     */
    addComment(
        communityItemId: string,
        comment: string
    ): Observable<CommunityCommentContract> {
        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'comments'
        );
        const params = new HttpParams().set('comment', comment);
        this.removeCaches(communityItemId);

        return this.http.post<CommunityCommentContract>(url, {}, { params });
    }

    /**
     * @param communityItemId
     * @param commentId
     * @param commentText
     * @description - PUT /community-items/{id}/comments/{commentId}
     */
    updateComment(
        communityItemId: string,
        commentId: string,
        commentText: string
    ): Observable<CommunityCommentContract> {
        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'comments',
            commentId
        );
        const params = new HttpParams().set('comment', commentText);
        this.removeCaches(communityItemId);

        return this.http.put<CommunityCommentContract>(url, {}, { params });
    }

    /**
     * @param communityItemId
     * @param commentId
     * @param comment
     * @description - POST /community-items/{id}/comments/{commentId}/replies
     */
    replyToComment(
        communityItemId: string,
        commentId: string,
        comment: string
    ): Observable<CommunityCommentContract> {
        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'comments',
            commentId,
            'replies'
        );
        const params = new HttpParams().set('comment', comment);
        this.removeCaches(communityItemId);

        return this.http.post<CommunityCommentContract>(url, {}, { params });
    }

    /**
     * @param communityItemId
     * @param commentId
     * @param replyId
     * @param comment
     * @description - PUT /community-items/{id}/comments/{commentId}/replies/{replyId}
     */
    updateReplyToComment(
        communityItemId: string,
        commentId: string,
        replyId: string,
        comment: string
    ): Observable<CommunityCommentContract> {
        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'comments',
            commentId,
            'replies',
            replyId
        );
        const params = new HttpParams().set('comment', comment);
        this.removeCaches(communityItemId);

        return this.http.put<CommunityCommentContract>(url, {}, { params });
    }

    /**
     * @param communityItemId
     * @param commentId
     * @description - DELETE /community-items/{id}/comments
     */
    deleteComment(
        communityItemId: string,
        commentId: string
    ): Observable<undefined> {
        const url = this.routingHelpersService.combineUrl(
            this.communityUrl,
            communityItemId,
            'comments'
        );
        const params = new HttpParams().set('commentId', commentId);
        this.removeCaches(communityItemId);

        return this.http.delete<undefined>(url, { params });
    }
    //#endregion

    removeCaches(communityItemId: string): void {
        this.communityItemCache[communityItemId] = undefined;
        const cacheIdRegex = new RegExp(`^${communityItemId}.*`);

        Object.keys(this.collectionContentCache).forEach((key) => {
            if (cacheIdRegex.test(key)) {
                this.collectionContentCache[key] = undefined;
            }
        });

        Object.keys(this.rideEventContentCache).forEach((key) => {
            if (cacheIdRegex.test(key)) {
                this.rideEventContentCache[key] = undefined;
            }
        });

        Object.keys(this.rideEventCollectionContentCache).forEach((key) => {
            if (cacheIdRegex.test(key)) {
                this.rideEventCollectionContentCache[key] = undefined;
            }
        });
    }
}
